import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase/firebaseConfig";

// Solo motores oficiales
import cargarEvaluacionEstudiante from "../funciones/cargarEvaluacionEstudiante";
import construirRutaAprendizaje from "../funciones/construirRutaAprendizaje";
import motorYuta from "../funciones/motores/motorYuta";

// ===================================
// UTILIDADES DE PRESENTACIÓN
// ===================================

const NOMBRE_EXAMEN = { 1: "ICFES", 2: "UNAL", 3: "UdeA" };

const normalizar = (valor) =>
  String(valor || "")
    .trim()
    .toLowerCase();

const porcentaje = (indice) => Math.round((indice || 0) * 100);

const aFecha = (valor) => {
  if (!valor) return null;
  if (typeof valor.toDate === "function") return valor.toDate();
  const fecha = new Date(valor);
  return isNaN(fecha.getTime()) ? null : fecha;
};

const textoHace = (fecha) => {
  if (!fecha) return "Sin registros";
  const dias = Math.floor((Date.now() - fecha.getTime()) / 86400000);
  if (dias <= 0) return "Hoy";
  if (dias === 1) return "Ayer";
  return `Hace ${dias} días`;
};

// Etiqueta y color oficiales de motorYuta
const etiquetaMotor = (indice) => motorYuta.nombreEstado(indice || 0);
const colorMotor = (indice) => motorYuta.colorEstado(indice || 0);

const ESTADOS_RUTA = {
  superado: { etiqueta: "Superado", simbolo: "✓" },
  en_curso: { etiqueta: "En progreso", simbolo: "◐" },
  listo: { etiqueta: "Listo para empezar", simbolo: "●" },
  pendiente: { etiqueta: "Espera prerrequisitos", simbolo: "○" },
};

// ===================================
// PIEZAS VISUALES
// ===================================

const NivelPuntos = ({ nivel = 0 }) => (
  <span className="gp-puntos" aria-label={`Nivel ${nivel} de 5`} title={`Nivel ${nivel} de 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <span key={n} className={`gp-punto ${n <= nivel ? "is-lleno" : ""}`} />
    ))}
  </span>
);

const BarraIndice = ({ indice, sinPractica }) => (
  <div className="gp-barra">
    <div className="gp-barra__pista">
      <div
        className="gp-barra__relleno"
        style={{
          width: sinPractica ? 0 : `${porcentaje(indice)}%`,
          background: colorMotor(indice),
        }}
      />
    </div>
    <span className="gp-barra__valor">
      {sinPractica ? "Sin práctica" : `${porcentaje(indice)}%`}
    </span>
  </div>
);

const Dato = ({ titulo, valor, detalle }) => (
  <div className="gp-dato">
    <span className="gp-dato__titulo">{titulo}</span>
    <span className="gp-dato__valor">{valor}</span>
    {detalle && <span className="gp-dato__detalle">{detalle}</span>}
  </div>
);

// ===================================
// COMPONENTE PRINCIPAL
// ===================================

const GrupoProfesor = () => {
  const { grupoId } = useParams();

  const [estudiantes, setEstudiantes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [busqueda, setBusqueda] = useState("");

  const [seleccionado, setSeleccionado] = useState("");
  const [evaluacion, setEvaluacion] = useState(null);
  const [cargandoEval, setCargandoEval] = useState(false);
  const [errorEval, setErrorEval] = useState("");

  const [pestana, setPestana] = useState("ruta");
  const [componenteRuta, setComponenteRuta] = useState(null);

  // ===================================
  // ESTUDIANTES DEL GRUPO
  // ===================================

  useEffect(() => {
    let activo = true;

    const cargar = async () => {
      setCargando(true);
      setError("");

      try {
        const miembrosSnap = await getDocs(
          query(
            collection(db, "grupoMiembros"),
            where("grupoId", "==", grupoId),
            where("estado", "==", "activo"),
            where("rol", "==", "estudiante")
          )
        );

        const lista = await Promise.all(
          miembrosSnap.docs.map(async (documento) => {
            const datos = documento.data();
            let nombre = datos.usuarioId;

            try {
              const usuarioSnap = await getDoc(doc(db, "usuarios", datos.usuarioId));
              if (usuarioSnap.exists()) {
                const u = usuarioSnap.data();
                nombre = u.nick || u.nombre || datos.usuarioId;
              }
            } catch (e) {
              // Si no hay permiso o falla, se muestra el identificador
            }

            return { id: documento.id, usuarioId: datos.usuarioId, nombre };
          })
        );

        if (!activo) return;

        setEstudiantes(
          lista.sort((a, b) => String(a.nombre).localeCompare(String(b.nombre)))
        );
      } catch (err) {
        console.error("Error al cargar estudiantes:", err);
        if (activo) {
          setError("No fue posible cargar los estudiantes. Recarga la página para intentarlo de nuevo.");
        }
      } finally {
        if (activo) setCargando(false);
      }
    };

    if (grupoId) {
      cargar();
    } else {
      setError("No se recibió el identificador del grupo.");
      setCargando(false);
    }

    return () => {
      activo = false;
    };
  }, [grupoId]);

  // ===================================
  // EVALUACIÓN CON EL MOTOR OFICIAL
  // ===================================

  useEffect(() => {
    let activo = true;

    setEvaluacion(null);
    setErrorEval("");
    setComponenteRuta(null);
    setPestana("ruta");

    if (!seleccionado) return undefined;

    setCargandoEval(true);

    cargarEvaluacionEstudiante(seleccionado)
      .then((resultado) => {
        if (!activo) return;
        if (!resultado) {
          setErrorEval("No se encontró el perfil de este estudiante.");
          return;
        }
        setEvaluacion(resultado);
      })
      .catch((err) => {
        console.error("Error evaluando al estudiante:", err);
        if (activo) {
          setErrorEval("No fue posible calcular el progreso de este estudiante. Revisa la conexión e inténtalo de nuevo.");
        }
      })
      .finally(() => {
        if (activo) setCargandoEval(false);
      });

    return () => {
      activo = false;
    };
  }, [seleccionado]);

  // ===================================
  // DATOS DERIVADOS (solo presentación)
  // ===================================

  const estudiantesFiltrados = estudiantes.filter((e) =>
    normalizar(e.nombre).includes(normalizar(busqueda))
  );

  const estudianteActual = estudiantes.find((e) => e.usuarioId === seleccionado);

  const tutor = evaluacion?.tutor;
  const usuario = evaluacion?.usuario;
  const desglose = evaluacion?.desglose;
  const examenId = usuario?.examenPrincipal;

  const componentesExamen = useMemo(() => {
    if (!desglose) return [];
    return Object.values(desglose.componentes)
      .filter((c) => Number(c.examenId) === Number(examenId))
      .sort((a, b) => String(a.nombre).localeCompare(String(b.nombre)));
  }, [desglose, examenId]);

  useEffect(() => {
    if (componentesExamen.length > 0 && componenteRuta === null) {
      setComponenteRuta(componentesExamen[0].id);
    }
  }, [componentesExamen, componenteRuta]);

  const ruta = useMemo(() => {
    if (!desglose || componenteRuta === null) return null;
    return construirRutaAprendizaje({ desglose, componenteId: componenteRuta });
  }, [desglose, componenteRuta]);

  const actividad = useMemo(() => {
    const evidencias = evaluacion?.evidencias || [];
    let ultima = null;
    const sesiones = new Set();

    evidencias.forEach((e) => {
      const fecha = aFecha(e.fecha);
      if (fecha && (!ultima || fecha > ultima)) ultima = fecha;
      if (e.sesionId) sesiones.add(e.sesionId);
    });

    return { total: evidencias.length, ultima, sesiones: sesiones.size };
  }, [evaluacion]);

  const avanceObjetivo =
    tutor?.puntajeObjetivo > 0
      ? Math.min(100, Math.round((tutor.puntajeEstimado / tutor.puntajeObjetivo) * 100))
      : 0;

  const verRutaDe = (idComponente) => {
    setComponenteRuta(idComponente);
    setPestana("ruta");
  };

  // ===================================
  // RENDER
  // ===================================

  return (
    <div className="gp">
      <style>{estilos}</style>

      <header className="gp-encabezado">
        <h1>Seguimiento del grupo</h1>
        <p className="gp-sutil">Grupo {grupoId}</p>
      </header>

      {!cargando && error && (
        <p role="alert" className="gp-alerta">{error}</p>
      )}

      <div className="gp-cuerpo">
        {/* ========== LISTA DE ESTUDIANTES ========== */}
        <aside className="gp-lateral" aria-label="Estudiantes del grupo">
          <div className="gp-lateral__titulo">
            <h2>Estudiantes</h2>
            <span className="gp-contador">{estudiantes.length}</span>
          </div>

          {cargando && <p className="gp-sutil">Cargando estudiantes…</p>}

          {!cargando && !error && estudiantes.length === 0 && (
            <p className="gp-sutil">Este grupo aún no tiene estudiantes activos.</p>
          )}

          {!cargando && estudiantes.length > 0 && (
            <>
              <input
                type="search"
                className="gp-buscador"
                placeholder="Buscar estudiante"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                aria-label="Buscar estudiante"
              />

              <ul className="gp-lista">
                {estudiantesFiltrados.map((estudiante) => {
                  const activo = estudiante.usuarioId === seleccionado;
                  return (
                    <li key={estudiante.id}>
                      <button
                        type="button"
                        className={`gp-alumno ${activo ? "is-activo" : ""}`}
                        aria-pressed={activo}
                        onClick={() => setSeleccionado(estudiante.usuarioId)}
                      >
                        {estudiante.nombre}
                      </button>
                    </li>
                  );
                })}
              </ul>

              {estudiantesFiltrados.length === 0 && (
                <p className="gp-sutil">Ningún estudiante coincide.</p>
              )}
            </>
          )}
        </aside>

        {/* ========== PANEL PRINCIPAL ========== */}
        <main className="gp-panel">
          {!seleccionado && !cargando && estudiantes.length > 0 && (
            <div className="gp-vacio">
              <p>Elige un estudiante de la lista para ver su progreso.</p>
            </div>
          )}

          {cargandoEval && (
            <div className="gp-vacio">
              <p>Calculando el progreso con el motor de evaluación…</p>
            </div>
          )}

          {errorEval && !cargandoEval && (
            <p role="alert" className="gp-alerta">{errorEval}</p>
          )}

          {evaluacion && tutor && !cargandoEval && (
            <>
              {/* ---------- RESUMEN ---------- */}
              <section className="gp-hero">
                <div className="gp-hero__cabecera">
                  <div>
                    <h2 className="gp-hero__nombre">
                      {estudianteActual?.nombre || usuario?.nick || usuario?.nombre || seleccionado}
                    </h2>
                    <p className="gp-sutil">
                      Se prepara para {usuario?.examenNombre || NOMBRE_EXAMEN[examenId] || "su examen"}
                      {tutor.carreraObjetivo?.nombre &&
                        `, con meta en ${tutor.carreraObjetivo.nombre}`}
                      {tutor.carreraObjetivo?.universidad &&
                        ` (${tutor.carreraObjetivo.universidad})`}
                    </p>
                  </div>

                  <span
                    className="gp-estado"
                    style={{ borderColor: tutor.estadoColor, color: "var(--gp-tinta)" }}
                  >
                    <span className="gp-estado__punto" style={{ background: tutor.estadoColor }} />
                    {tutor.estado}
                  </span>
                </div>

                <div className="gp-meta">
                  <div className="gp-meta__cifras">
                    <span className="gp-meta__actual">{tutor.puntajeEstimado}</span>
                    <span className="gp-sutil">
                      puntaje estimado
                      {tutor.puntajeObjetivo > 0 && ` de ${tutor.puntajeObjetivo} que pide su carrera`}
                    </span>
                  </div>
                  {tutor.puntajeObjetivo > 0 && (
                    <div className="gp-meta__pista" aria-hidden="true">
                      <div className="gp-meta__relleno" style={{ width: `${avanceObjetivo}%` }} />
                    </div>
                  )}
                </div>

                <div className="gp-datos">
                  <Dato titulo="Probabilidad de admisión" valor={`${tutor.probabilidad ?? 0}%`} />
                  <Dato titulo="Dominio general" valor={`${tutor.porcentajeDominio ?? 0}%`} detalle={tutor.nivel} />
                  <Dato titulo="Última práctica" valor={textoHace(actividad.ultima)} />
                  <Dato
                    titulo="Respuestas registradas"
                    valor={actividad.total}
                    detalle={`${actividad.sesiones} ${actividad.sesiones === 1 ? "sesión" : "sesiones"}`}
                  />
                </div>
              </section>

              {!desglose && (
                <p role="alert" className="gp-alerta">
                  El motor no devolvió el desglose por tema y componente. Verifica que esté aplicado el cambio en motorEvaluacion.js.
                </p>
              )}

              {/* ---------- PESTAÑAS ---------- */}
              <div className="gp-pestanas" role="tablist">
                {[
                  ["ruta", "Ruta de aprendizaje"],
                  ["componentes", "Componentes"],
                  ["diagnostico", "Diagnóstico y prioridades"],
                ].map(([clave, texto]) => (
                  <button
                    key={clave}
                    type="button"
                    role="tab"
                    aria-selected={pestana === clave}
                    className={`gp-pestana ${pestana === clave ? "is-activo" : ""}`}
                    onClick={() => setPestana(clave)}
                  >
                    {texto}
                  </button>
                ))}
              </div>

              {/* ---------- RUTA DE APRENDIZAJE ---------- */}
              {pestana === "ruta" && desglose && (
                <section>
                  <div className="gp-chips" role="tablist" aria-label="Componentes">
                    {componentesExamen.map((c) => {
                      const activo = String(c.id) === String(componenteRuta);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          role="tab"
                          aria-selected={activo}
                          className={`gp-chip ${activo ? "is-activo" : ""}`}
                          onClick={() => setComponenteRuta(c.id)}
                        >
                          {c.nombre}
                        </button>
                      );
                    })}
                  </div>

                  {ruta && ruta.resumen.total === 0 && (
                    <div className="gp-vacio">
                      <p>Este componente no tiene temas configurados.</p>
                    </div>
                  )}

                  {ruta && ruta.resumen.total > 0 && (
                    <>
                      <div className="gp-leyenda">
                        {Object.entries(ESTADOS_RUTA).map(([clave, info]) => {
                          const cantidad = {
                            superado: ruta.resumen.superados,
                            en_curso: ruta.resumen.enCurso,
                            listo: ruta.resumen.listos,
                            pendiente: ruta.resumen.pendientes,
                          }[clave];
                          return (
                            <span key={clave} className={`gp-leyenda__item gp-ruta-${clave}`}>
                              <span className="gp-simbolo">{info.simbolo}</span>
                              {info.etiqueta}: <strong>{cantidad}</strong>
                            </span>
                          );
                        })}
                      </div>

                      {ruta.siguientes.length > 0 && (
                        <div className="gp-siguiente">
                          <h3>Siguiente paso según los prerrequisitos</h3>
                          <ul>
                            {ruta.siguientes.map((t) => (
                              <li key={t.id}>
                                <strong>{t.nombre}</strong>
                                <span className="gp-sutil">
                                  {t.estadoRuta === "en_curso"
                                    ? ` Ya lo empezó; está en "${etiquetaMotor(t.indice)}".`
                                    : " Ya cumple los prerrequisitos y aún no lo practica."}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <ol className="gp-ruta">
                        {ruta.etapas.map((etapa) => (
                          <li key={etapa.numero} className="gp-etapa">
                            <div className="gp-etapa__marca" aria-hidden="true">
                              {etapa.numero}
                            </div>
                            <div className="gp-etapa__contenido">
                              <h3 className="gp-etapa__titulo">
                                Etapa {etapa.numero}
                                <span className="gp-sutil">
                                  {etapa.numero === 1
                                    ? " Temas base, sin prerrequisitos en este componente"
                                    : " Se apoya en las etapas anteriores"}
                                </span>
                              </h3>

                              <div className="gp-nodos">
                                {etapa.temas.map((t) => (
                                  <article key={t.id} className={`gp-nodo gp-ruta-${t.estadoRuta}`}>
                                    <div className="gp-nodo__cabecera">
                                      <span className="gp-simbolo" aria-hidden="true">
                                        {ESTADOS_RUTA[t.estadoRuta].simbolo}
                                      </span>
                                      <span className="gp-nodo__nombre">{t.nombre}</span>
                                    </div>

                                    <span className="gp-nodo__estado">
                                      {ESTADOS_RUTA[t.estadoRuta].etiqueta}
                                    </span>

                                    {t.totalEvidencias > 0 && (
                                      <div className="gp-nodo__metricas">
                                        <NivelPuntos nivel={t.nivel} />
                                        <span className="gp-sutil">
                                          {etiquetaMotor(t.indice)}, {t.totalEvidencias}{" "}
                                          {t.totalEvidencias === 1 ? "respuesta" : "respuestas"}
                                        </span>
                                      </div>
                                    )}

                                    {t.prerrequisitosPendientes.length > 0 && (
                                      <p className="gp-nodo__requiere">
                                        Requiere: {t.prerrequisitosPendientes.map((p) => p.nombre).join(", ")}
                                      </p>
                                    )}
                                  </article>
                                ))}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </>
                  )}
                </section>
              )}

              {/* ---------- COMPONENTES ---------- */}
              {pestana === "componentes" && desglose && (
                <section className="gp-grid-comp">
                  {componentesExamen.map((c) => {
                    const sinPractica = c.temasEvaluados === 0;
                    return (
                      <article key={c.id} className="gp-tarjeta">
                        <h3 className="gp-tarjeta__titulo">{c.nombre}</h3>
                        <BarraIndice indice={c.indice} sinPractica={sinPractica} />
                        <div className="gp-tarjeta__fila">
                          <span>{sinPractica ? "Aún sin práctica" : etiquetaMotor(c.indice)}</span>
                          {!sinPractica && <NivelPuntos nivel={c.nivel} />}
                        </div>
                        <p className="gp-sutil">
                          Ha practicado {c.temasEvaluados} de {c.totalTemas} temas
                        </p>
                        <button type="button" className="gp-enlace" onClick={() => verRutaDe(c.id)}>
                          Ver su ruta de aprendizaje
                        </button>
                      </article>
                    );
                  })}
                </section>
              )}

              {/* ---------- DIAGNÓSTICO (igual al panel del estudiante) ---------- */}
              {pestana === "diagnostico" && (
                <section className="gp-dos-columnas">
                  <div className="gp-tarjeta">
                    <h3 className="gp-tarjeta__titulo">Diagnóstico por materia</h3>
                    <p className="gp-sutil">Lo mismo que el estudiante ve en su panel.</p>
                    {(tutor.diagnostico || []).length === 0 ? (
                      <p className="gp-sutil">Aún no hay datos por materia.</p>
                    ) : (
                      <ul className="gp-lista-simple">
                        {tutor.diagnostico.map((d) => (
                          <li key={d.componente}>
                            <span className="gp-lista-simple__nombre">{d.componente}</span>
                            <BarraIndice indice={(d.dominio || 0) / 100} />
                            <span className="gp-sutil">{String(d.tendencia || "").toLowerCase()}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="gp-tarjeta">
                    <h3 className="gp-tarjeta__titulo">Temas prioritarios</h3>
                    <p className="gp-sutil">Los que el panel del estudiante le recomienda reforzar.</p>
                    {(tutor.temasPrioritarios || []).length === 0 ? (
                      <p className="gp-sutil">Sin temas prioritarios por ahora.</p>
                    ) : (
                      <ul className="gp-lista-simple">
                        {tutor.temasPrioritarios.map((t) => (
                          <li key={t.id}>
                            <span className="gp-lista-simple__nombre">{t.nombre}</span>
                            <span className="gp-sutil">
                              {t.materia && `${t.materia}. `}Motivo: {t.explicacion}.
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

// ===================================
// ESTILOS (prefijo gp-)
// ===================================

const estilos = `
.gp {
  --gp-tinta: #1d2a3a;
  --gp-sutil: #5f6b7a;
  --gp-linea: #dfe4ea;
  --gp-fondo: #f6f8fa;
  --gp-superficie: #ffffff;
  --gp-acento: #23577a;
  --gp-superado: #2f7d4f;
  --gp-curso: #b7791f;
  --gp-listo: #23577a;
  --gp-pendiente: #9aa5b1;

  max-width: 1180px;
  margin: 32px auto;
  padding: 0 20px;
  color: var(--gp-tinta);
  font-family: inherit;
  line-height: 1.45;
}
.gp h1, .gp h2, .gp h3 { margin: 0; }
.gp-encabezado { margin-bottom: 24px; }
.gp-encabezado h1 { font-size: 1.6rem; font-weight: 700; }
.gp-sutil { color: var(--gp-sutil); font-size: 0.9rem; margin: 0; }
.gp-alerta { background: #fdecea; color: #b42318; padding: 10px 14px; border-radius: 6px; margin: 0 0 16px; }

.gp-cuerpo { display: flex; flex-wrap: wrap; gap: 24px; align-items: flex-start; }

/* Lateral */
.gp-lateral {
  flex: 1 1 230px; max-width: 290px;
  background: var(--gp-fondo); border: 1px solid var(--gp-linea);
  border-radius: 10px; padding: 16px; position: sticky; top: 16px;
}
.gp-lateral__titulo { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.gp-lateral__titulo h2 { font-size: 1rem; }
.gp-contador { background: var(--gp-superficie); border: 1px solid var(--gp-linea); border-radius: 999px; padding: 0 10px; font-size: 0.85rem; }
.gp-buscador {
  width: 100%; box-sizing: border-box; padding: 8px 10px; margin-bottom: 10px;
  border: 1px solid var(--gp-linea); border-radius: 6px; font: inherit; background: var(--gp-superficie);
}
.gp-lista { list-style: none; margin: 0; padding: 0; max-height: 60vh; overflow-y: auto; }
.gp-alumno {
  width: 100%; text-align: left; background: transparent; border: 0;
  border-left: 3px solid transparent; padding: 8px 10px; font: inherit; color: var(--gp-tinta);
  cursor: pointer; border-radius: 0 6px 6px 0; overflow-wrap: anywhere;
}
.gp-alumno:hover { background: var(--gp-superficie); }
.gp-alumno.is-activo { background: var(--gp-superficie); border-left-color: var(--gp-acento); font-weight: 600; }

/* Panel */
.gp-panel { flex: 3 1 500px; min-width: 0; }
.gp-vacio {
  border: 1px dashed var(--gp-linea); border-radius: 10px; padding: 32px 20px;
  text-align: center; color: var(--gp-sutil);
}
.gp-vacio p { margin: 0; }

/* Resumen */
.gp-hero {
  border: 1px solid var(--gp-linea); border-radius: 12px; padding: 22px 24px;
  background: var(--gp-superficie); margin-bottom: 20px;
}
.gp-hero__cabecera { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap; }
.gp-hero__nombre { font-size: 1.4rem; margin-bottom: 4px; overflow-wrap: anywhere; }
.gp-estado {
  display: inline-flex; align-items: center; gap: 8px; border: 1.5px solid;
  border-radius: 999px; padding: 4px 12px; font-size: 0.9rem; font-weight: 600; white-space: nowrap;
}
.gp-estado__punto { width: 9px; height: 9px; border-radius: 50%; }

.gp-meta { margin: 22px 0 18px; }
.gp-meta__cifras { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.gp-meta__actual { font-size: 2.6rem; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums; }
.gp-meta__pista { height: 8px; background: #edf0f3; border-radius: 999px; overflow: hidden; margin-top: 12px; }
.gp-meta__relleno { height: 100%; background: var(--gp-acento); border-radius: 999px; }

.gp-datos { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; }
.gp-dato { background: var(--gp-fondo); border-radius: 8px; padding: 10px 12px; display: flex; flex-direction: column; gap: 2px; }
.gp-dato__titulo { font-size: 0.82rem; color: var(--gp-sutil); }
.gp-dato__valor { font-size: 1.15rem; font-weight: 600; }
.gp-dato__detalle { font-size: 0.82rem; color: var(--gp-sutil); }

/* Pestañas y chips */
.gp-pestanas { display: flex; gap: 4px; border-bottom: 1px solid var(--gp-linea); margin-bottom: 16px; overflow-x: auto; }
.gp-pestana {
  background: none; border: 0; border-bottom: 2px solid transparent; padding: 10px 14px;
  font: inherit; color: var(--gp-sutil); cursor: pointer; margin-bottom: -1px; white-space: nowrap;
}
.gp-pestana.is-activo { color: var(--gp-tinta); border-bottom-color: var(--gp-acento); font-weight: 600; }
.gp-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
.gp-chip {
  border: 1px solid var(--gp-linea); background: var(--gp-superficie); border-radius: 999px;
  padding: 6px 14px; font: inherit; font-size: 0.92rem; color: var(--gp-tinta); cursor: pointer;
}
.gp-chip:hover { border-color: var(--gp-acento); }
.gp-chip.is-activo { background: var(--gp-acento); border-color: var(--gp-acento); color: #fff; }

/* Leyenda y siguiente paso */
.gp-leyenda { display: flex; flex-wrap: wrap; gap: 8px 18px; margin-bottom: 16px; font-size: 0.9rem; }
.gp-leyenda__item { display: inline-flex; align-items: center; gap: 6px; }
.gp-simbolo { font-weight: 700; width: 1em; text-align: center; color: var(--gp-color-estado); }

.gp-siguiente {
  border-left: 3px solid var(--gp-acento); background: #eef4f8;
  padding: 12px 16px; border-radius: 0 8px 8px 0; margin-bottom: 22px;
}
.gp-siguiente h3 { font-size: 0.95rem; margin-bottom: 6px; }
.gp-siguiente ul { margin: 0; padding-left: 18px; }
.gp-siguiente li { margin: 3px 0; }

/* Colores por estado de ruta */
.gp-ruta-superado { --gp-color-estado: var(--gp-superado); }
.gp-ruta-en_curso { --gp-color-estado: var(--gp-curso); }
.gp-ruta-listo { --gp-color-estado: var(--gp-listo); }
.gp-ruta-pendiente { --gp-color-estado: var(--gp-pendiente); }

/* Ruta por etapas */
.gp-ruta { list-style: none; margin: 0; padding: 0; }
.gp-etapa { display: grid; grid-template-columns: 36px 1fr; gap: 14px; position: relative; padding-bottom: 22px; }
.gp-etapa:not(:last-child)::before {
  content: ""; position: absolute; left: 17px; top: 36px; bottom: 0; width: 2px; background: var(--gp-linea);
}
.gp-etapa__marca {
  width: 36px; height: 36px; border-radius: 50%; display: grid; place-items: center;
  background: var(--gp-superficie); border: 2px solid var(--gp-acento); color: var(--gp-acento); font-weight: 700;
}
.gp-etapa__titulo { font-size: 1rem; margin: 6px 0 10px; }
.gp-nodos { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 10px; }
.gp-nodo {
  background: var(--gp-superficie); border: 1px solid var(--gp-linea);
  border-left: 4px solid var(--gp-color-estado); border-radius: 8px; padding: 10px 12px;
  display: flex; flex-direction: column; gap: 4px;
}
.gp-nodo.gp-ruta-pendiente { background: var(--gp-fondo); }
.gp-nodo__cabecera { display: flex; gap: 6px; align-items: flex-start; }
.gp-nodo__nombre { font-weight: 600; }
.gp-nodo__estado { font-size: 0.82rem; color: var(--gp-color-estado); font-weight: 600; }
.gp-nodo__metricas { display: flex; flex-direction: column; gap: 4px; margin-top: 2px; }
.gp-nodo__requiere { font-size: 0.82rem; color: var(--gp-sutil); margin: 4px 0 0; }

.gp-puntos { display: inline-flex; gap: 3px; }
.gp-punto { width: 8px; height: 8px; border-radius: 50%; background: #e3e7ec; }
.gp-punto.is-lleno { background: var(--gp-acento); }

/* Componentes */
.gp-grid-comp { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 14px; }
.gp-tarjeta {
  background: var(--gp-superficie); border: 1px solid var(--gp-linea); border-radius: 10px;
  padding: 16px; display: flex; flex-direction: column; gap: 10px;
}
.gp-tarjeta__titulo { font-size: 1rem; }
.gp-tarjeta__fila { display: flex; justify-content: space-between; align-items: center; gap: 8px; font-size: 0.92rem; }
.gp-enlace {
  align-self: flex-start; background: none; border: 0; padding: 0; font: inherit;
  font-size: 0.9rem; color: var(--gp-acento); cursor: pointer;
}
.gp-enlace:hover { text-decoration: underline; }

.gp-barra { display: flex; align-items: center; gap: 10px; }
.gp-barra__pista { flex: 1; height: 8px; background: #edf0f3; border-radius: 999px; overflow: hidden; min-width: 60px; }
.gp-barra__relleno { height: 100%; border-radius: 999px; }
.gp-barra__valor { font-size: 0.88rem; font-variant-numeric: tabular-nums; min-width: 80px; }

/* Diagnóstico */
.gp-dos-columnas { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 14px; }
.gp-lista-simple { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px; }
.gp-lista-simple li { display: flex; flex-direction: column; gap: 4px; }
.gp-lista-simple__nombre { font-weight: 600; }

/* Foco */
.gp button:focus-visible, .gp input:focus-visible {
  outline: 2px solid var(--gp-acento); outline-offset: 2px;
}

@media (max-width: 760px) {
  .gp-lateral { max-width: none; position: static; }
  .gp-lista { max-height: 220px; }
  .gp-meta__actual { font-size: 2.1rem; }
}
`;

export default GrupoProfesor;